import React, { forwardRef, useEffect } from 'react';

import { SmallLoader } from 'components/Loader';
import Tooltip from 'components/common/Tooltip';
import { isOrderCancellable, isOrderExchangeable, isOrderReturnable } from 'helpers/MyAccountUtils';
import useMartyContext from 'hooks/useMartyContext';
import { RETURN_INITIATE_SOURCE } from 'constants/amethystEnums';
import { ORDER_ACTION_NONE, ORDER_PAGE_STEPS } from 'constants/orderActions';
import { cn } from 'helpers/classnames';
import { useFlag } from 'containers/UnleashFlagProvider';
import { FEATURE_EXCHANGES_DISABLE } from 'constants/features';

import css from 'styles/components/account/order/orderTableActionButtonsHeader.scss';

export const SELECT_ITEM_TO_EXCHANGE = 'Select Item To Exchange';
export const SELECT_ITEMS_TO_RETURN = 'Select Item(s) To Return';
export const EXCHANGE_SELECTED_ITEM = 'Exchange Selected Item';
export const RETURN_SELECTED_ITEMS = 'Return Selected Items';
export const CANCEL_CHECKED_ITEMS = 'Cancel Checked Items';
export const CANCEL = 'Cancel';

const ReturnStepActions = ({ cancelAction, returnSubmitAction, selectedItems, isOrderItemsUpdating, testId }) => (
  <>
    <button
      className={css.validationButton}
      type="button"
      onClick={returnSubmitAction}
      disabled={!selectedItems.length}
      data-test-id={testId('submitReturnButton')}
    >
      {RETURN_SELECTED_ITEMS}
    </button>
    {isOrderItemsUpdating ? (
      <SmallLoader additionalClassNames={css.loader} />
    ) : (
      <button
        type="button"
        onClick={cancelAction}
        disabled={isOrderItemsUpdating}
        aria-label="Cancel return"
        data-test-id={testId('cancelActionButton')}
      >
        {CANCEL}
      </button>
    )}
  </>
);

const ExchangeStepActions = forwardRef(({ cancelAction, exchangeSubmitAction, selectedItems, isOrderItemsUpdating, testId }, ref) => (
  <>
    <button
      className={css.validationButton}
      type="button"
      onClick={exchangeSubmitAction}
      disabled={!selectedItems.length}
      ref={ref}
      data-test-id={testId('submitExchangeButton')}
    >
      {EXCHANGE_SELECTED_ITEM}
    </button>
    {isOrderItemsUpdating ? (
      <SmallLoader additionalClassNames={css.loader} />
    ) : (
      <button type="button" onClick={cancelAction} aria-label="Cancel exchange" data-test-id={testId('cancelActionButton')}>
        {CANCEL}
      </button>
    )}
  </>
));

const DefaultStepActions = ({
  exchangeSelectionAction,
  returnSelectionAction,
  cancelItemsSubmitAction,
  isCancellable,
  isExchangeable,
  isReturnable,
  selectedItems,
  canExchange,
  testId,
  isExchangesDisabled
}) => (
  <>
    {isCancellable && (
      <button
        type="button"
        className={css.validationButton}
        onClick={cancelItemsSubmitAction}
        disabled={!selectedItems.length}
        data-test-id={testId('cancelItemsButton')}
      >
        {CANCEL_CHECKED_ITEMS}
      </button>
    )}
    {!isCancellable && isExchangeable && canExchange && !isExchangesDisabled && (
      <button type="button" onClick={exchangeSelectionAction} data-test-id={testId('exchangeMenuButton')}>
        {SELECT_ITEM_TO_EXCHANGE}
      </button>
    )}
    {!isCancellable && isReturnable && (
      <button
        type="button"
        onClick={returnSelectionAction}
        data-test-id={testId('returnMenuButton')}
        data-amethyst-return-behavior-survey={RETURN_INITIATE_SOURCE.ORDER_PAGE_SELECT_ITEMS_BUTTON}
      >
        {SELECT_ITEMS_TO_RETURN}
      </button>
    )}
  </>
);

const HeaderContent = forwardRef((props, ref) => {
  // handle refs for cancel and return pages here.
  const { currentStep, isExchangesDisabled } = props;
  const exchangeItemButtonRef = ref?.exchangeItemButtonRef;
  if (currentStep === ORDER_PAGE_STEPS.RETURN_STEP) {
    return <ReturnStepActions {...props} />;
  } else if (currentStep === ORDER_PAGE_STEPS.EXCHANGE_STEP && !isExchangesDisabled) {
    return <ExchangeStepActions {...props} ref={exchangeItemButtonRef} />;
  } else {
    return <DefaultStepActions {...props} />;
  }
});

const OrderTableActionButtonsHeader = forwardRef((props, ref) => {
  const { order, previousAction, currentStep, keepExchangeOrdersFromReturnCancel, canExchange, updateOrderItems, isOrderItemsUpdating } = props;
  const { applePayOrder, paypalOrder } = order;
  const { testId } = useMartyContext();

  // Feature flag to disable exchanges
  const isExchangesDisabled = useFlag(FEATURE_EXCHANGES_DISABLE);

  const isCancellable = isOrderCancellable(order.lineItems, keepExchangeOrdersFromReturnCancel);
  const isExchangeable = isOrderExchangeable(order.lineItems);
  const isReturnable = isOrderReturnable(order.lineItems, keepExchangeOrdersFromReturnCancel);

  // Make sure that the order items statuses is up to date when it mounts
  useEffect(() => {
    if (previousAction !== ORDER_ACTION_NONE && currentStep === ORDER_PAGE_STEPS.DEFAULT_STEP) {
      updateOrderItems();
    }
  }, [previousAction, currentStep]);

  const paymentTypeText = applePayOrder ? 'Apple Pay' : 'PayPal';

  return (
    <div className={css.orderTableButtonsHeaderContainer}>
      {(applePayOrder || paypalOrder) && !isExchangesDisabled && (
        <div className={cn(css.info, 'zappos:body-s')}>
          <b>Exchanges</b> are unavailable for this order.
          <Tooltip
            tooltipClassName={cn(css.tooltip, 'zappos:body-m')}
            tooltipId="Exchange Learn More"
            direction="bottom"
            content={`Orders paid with ${paymentTypeText} are ineligible for exchanges at this time. Please return the item(s) here and place a new order.`}
          >
            <span className={cn(css.tooltipCta, 'zappos:body-s-medium')}>Learn More</span>
          </Tooltip>
        </div>
      )}
      <HeaderContent
        {...props}
        isCancellable={isCancellable}
        isExchangeable={isExchangeable}
        isReturnable={isReturnable}
        isOrderItemsUpdating={isOrderItemsUpdating}
        testId={testId}
        ref={ref}
        canExchange={canExchange}
        isExchangesDisabled={isExchangesDisabled}
      />
    </div>
  );
});

export default OrderTableActionButtonsHeader;
